import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, Plus, RefreshCw, Sparkles, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadixSelect } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RichTextEditor } from "@/components/elements/rich-text-editor";
import { CUSTOM_SCROLLBAR, TOAST_DURATION } from "@/lib/constants";
import { useUser } from "@/hooks/use-user";
import {
  useCreateJiraIssue,
  useDraftJiraTicket,
  useListJiraAssignableUsers,
  useListJiraBoards,
  type ChatTranscriptTurn,
} from "@/service-hooks/use-jira-issue-service";

// Sentinel select value for the "add a board" action item. Boards are numeric
// ids as strings, so this can never collide with a real board.
const ADD_BOARD_VALUE = "__add_board__";

// Jira has no fixed global set of issue types — each project defines its own.
// These three exist on virtually every team-managed project's default screens.
// Epic is deliberately absent: it usually requires fields this dialog does not
// collect (Epic Name), so offering it would mostly produce failed creates.
// TODO(follow-up): fetch the real per-board types from Jira's createmeta.
const ISSUE_TYPE_OPTIONS = [
  { value: "Task", label: "Task" },
  { value: "Story", label: "Story" },
  { value: "Bug", label: "Bug" },
];

// Matches the backend's own fallback, so not touching the dropdown behaves
// exactly as it did before this field existed.
const DEFAULT_ISSUE_TYPE = "Task";

// Sentinel select value meaning "no assignee". Empty string cannot be used —
// RadixSelect treats it as unselected and shows the placeholder — and no
// Atlassian accountId is ever this literal string.
const UNASSIGNED_VALUE = "__unassigned__";

const PRIORITY_OPTIONS = [
  { value: "Highest", label: "Highest" },
  { value: "High", label: "High" },
  { value: "Medium", label: "Medium" },
  { value: "Low", label: "Low" },
  { value: "Lowest", label: "Lowest" },
];

const AVAILABLE_EXTRA_FIELDS = [
  { key: "priority", label: "Priority" },
  { key: "labels", label: "Labels" },
  { key: "environment", label: "Environment" },
];

interface RaiseJiraTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectUuid: string;
  /** Trailing conversation turns, chronological, ending at the clicked message. */
  messages: ChatTranscriptTurn[];
  /** Clicked message's copyable text — the fallback body if drafting fails. */
  fallbackText: string;
}

export default function RaiseJiraTicketDialog({
  open,
  onOpenChange,
  projectUuid,
  messages,
  fallbackText,
}: RaiseJiraTicketDialogProps) {
  const [boardId, setBoardId] = useState("");
  const [issueType, setIssueType] = useState(DEFAULT_ISSUE_TYPE);
  const [assignee, setAssignee] = useState(UNASSIGNED_VALUE);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [activeFields, setActiveFields] = useState<string[]>([]);
  const [priority, setPriority] = useState("Medium");
  const [labelsInput, setLabelsInput] = useState("");
  const [environment, setEnvironment] = useState("");
  const [draftFailed, setDraftFailed] = useState(false);
  const [hasDrafted, setHasDrafted] = useState(false);
  const [lastDraftedType, setLastDraftedType] = useState<string>("");

  const navigate = useNavigate();
  const { user } = useUser();
  const isSystemAdmin = user?.role === "ADMIN";

  const { data: boards = [], isLoading: isLoadingBoards } = useListJiraBoards(
    open ? projectUuid : undefined,
  );

  // Only fetched once a board is picked — the endpoint is scoped per board and
  // there is nothing meaningful to show before then.
  const numericBoardId = boardId ? Number(boardId) : undefined;
  const {
    data: assignableUsers = [],
    isLoading: isLoadingAssignees,
    isError: assigneesFailed,
  } = useListJiraAssignableUsers(
    open ? projectUuid : undefined,
    open ? numericBoardId : undefined,
  );

  const { mutate: draftTicket, isPending: isDrafting } = useDraftJiraTicket();

  const { mutate: createIssue, isPending: isCreating } = useCreateJiraIssue(
    (issue) => {
      toast.success(`Created ${issue.key}`, {
        description: "Opens in Jira in a new tab.",
        duration: TOAST_DURATION,
        action: {
          label: "Open",
          onClick: () => window.open(issue.url, "_blank", "noopener,noreferrer"),
        },
      });
      onOpenChange(false);
    },
    (error) => {
      // Stay open so the user's edits survive a failed create — a required
      // custom field or a bad issue type is usually fixable in place.
      toast.error(error.message || "Failed to create the Jira ticket.", {
        duration: TOAST_DURATION,
      });
    },
  );

  // Reset state each time the dialog opens. Generation is decoupled from modal opening
  // so the user can choose the board, issue type (Bug/Story/Task), and assignee first.
  useEffect(() => {
    if (!open) return;

    setDraftFailed(false);
    setHasDrafted(false);
    setLastDraftedType("");
    setTitle("");
    setDescription("");
    setIsDescriptionOpen(false);
    setActiveFields([]);
    setPriority("Medium");
    setLabelsInput("");
    setEnvironment("");
    setIssueType(DEFAULT_ISSUE_TYPE);
    setAssignee(UNASSIGNED_VALUE);
  }, [open]);

  // Preselect when there is only one board — there is nothing to choose.
  useEffect(() => {
    if (boards.length === 1) {
      setBoardId(String(boards[0].id));
    }
  }, [boards]);

  const handleGenerateDraft = (targetIssueType: string = issueType) => {
    setDraftFailed(false);
    setIsDescriptionOpen(true);
    draftTicket(
      { projectUuid, messages, issueType: targetIssueType },
      {
        onSuccess: (draft) => {
          setTitle(draft.title);
          setDescription(draft.description);
          setHasDrafted(true);
          setLastDraftedType(targetIssueType);
          setIsDescriptionOpen(true);
        },
        onError: () => {
          // Not fatal. Fall back to the clicked message's text so the user can
          // still file the ticket by hand.
          setDraftFailed(true);
          setDescription(fallbackText);
          setHasDrafted(true);
          setLastDraftedType(targetIssueType);
          setIsDescriptionOpen(true);
        },
      },
    );
  };

  const availableFields = AVAILABLE_EXTRA_FIELDS.filter(
    (field) => !activeFields.includes(field.key),
  );

  const handleAddField = (fieldKey: string) => {
    if (!activeFields.includes(fieldKey)) {
      setActiveFields((prev) => [...prev, fieldKey]);
    }
  };

  const handleRemoveField = (fieldKey: string) => {
    setActiveFields((prev) => prev.filter((k) => k !== fieldKey));
    if (fieldKey === "priority") setPriority("Medium");
    if (fieldKey === "labels") setLabelsInput("");
    if (fieldKey === "environment") setEnvironment("");
  };

  const isBusy = isDrafting || isCreating;
  const canSubmit = !!boardId && title.trim() !== "" && !isBusy;

  // Navigating away from an open dialog rather than selecting a board would
  // leave a stale draft behind when the user comes back, so close it first.
  const handleBoardChange = (value: string) => {
    if (value === ADD_BOARD_VALUE) {
      onOpenChange(false);
      navigate("/settings?tab=jira");
      return;
    }
    setBoardId(value);
    // The assignable-users list is per-board — a name valid for board A may not
    // exist on board B, so reset rather than send an accountId Jira will reject.
    setAssignee(UNASSIGNED_VALUE);
  };

  const handleCreate = () => {
    if (!canSubmit) return;

    const parsedLabels = activeFields.includes("labels")
      ? labelsInput
          .split(",")
          .map((l) => l.trim())
          .filter(Boolean)
      : undefined;

    createIssue({
      projectUuid,
      jiraConfigId: Number(boardId),
      title: title.trim(),
      description: isDescriptionOpen && description.trim() ? description.trim() : undefined,
      issueType,
      assignee: assignee === UNASSIGNED_VALUE ? undefined : assignee,
      priority: activeFields.includes("priority") && priority ? priority : undefined,
      labels: parsedLabels && parsedLabels.length > 0 ? parsedLabels : undefined,
      environment: activeFields.includes("environment") && environment.trim() ? environment.trim() : undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isCreating && onOpenChange(next)}>
      {/* h-[90vh] rather than max-h: the description editor can only flex to
          fill the leftover space if the dialog has a definite height. gap-0 p-0
          cancels the primitive's own gap-4 p-6 so each region owns its padding. */}
      <DialogContent className="bg-surface text-text-primary w-[calc(100%-2rem)] max-w-4xl h-[90vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-4 sm:px-6 pt-6 pb-4">
          <DialogTitle className="pr-8 text-text-primary">Raise Jira Ticket</DialogTitle>
        </DialogHeader>

        {/* min-h-0 is load-bearing: without it a flex child will not shrink
            below its content height and the scroll never engages. */}
        <div
          className={`flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto px-4 sm:px-6 pb-6 ${CUSTOM_SCROLLBAR}`}
        >
          {draftFailed && (
            <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3">
              <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted">
                Auto-drafting wasn't available, so the description below is the
                response text. Add a title and edit as needed before creating.
              </p>
            </div>
          )}

          {/* Board, Issue Type, and Assignee share a row: all three are short
              controls, and the vertical space saved goes to the description
              editor below. */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="block text-sm font-medium text-text-primary mb-1">
                Board *
              </Label>
              {isLoadingBoards ? (
                <div className="flex items-center gap-2 text-sm text-muted h-10">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading boards…
                </div>
              ) : boards.length === 0 ? (
                <p className="text-sm text-muted h-10 flex items-center">
                  No Jira board is linked for this project.
                </p>
              ) : (
                <RadixSelect
                  value={boardId}
                  onChange={handleBoardChange}
                  placeholder="Select a board"
                  disabled={isBusy}
                  options={[
                    ...boards.map((board) => ({
                      value: String(board.id),
                      label: board.label,
                    })),
                    ...(isSystemAdmin
                      ? [{ value: ADD_BOARD_VALUE, label: "+ Add new board", emphasized: true }]
                      : []),
                  ]}
                />
              )}
            </div>

            <div>
              <Label className="block text-sm font-medium text-text-primary mb-1">
                Issue Type *
              </Label>
              <RadixSelect
                value={issueType}
                onChange={setIssueType}
                placeholder="Select an issue type"
                disabled={isBusy}
                options={ISSUE_TYPE_OPTIONS}
              />
            </div>

            <div>
              <Label className="block text-sm font-medium text-text-primary mb-1">
                Assignee
              </Label>
              {!boardId ? (
                <p className="text-sm text-muted h-10 flex items-center">
                  Select a board first.
                </p>
              ) : isLoadingAssignees ? (
                <div className="flex items-center gap-2 text-sm text-muted h-10">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading assignees…
                </div>
              ) : assigneesFailed ? (
                <p className="text-sm text-muted h-10 flex items-center">
                  Couldn't load assignees.
                </p>
              ) : (
                <RadixSelect
                  value={assignee}
                  onChange={setAssignee}
                  placeholder="Unassigned"
                  disabled={isBusy}
                  options={[
                    { value: UNASSIGNED_VALUE, label: "Unassigned" },
                    ...assignableUsers.map((user) => ({
                      value: user.accountId,
                      // Email disambiguates same-named users. Fall back to just
                      // the display name when the API doesn't return an email
                      // (common for GDPR-restricted accounts).
                      label: user.emailAddress
                        ? `${user.displayName} (${user.emailAddress})`
                        : user.displayName,
                    })),
                  ]}
                />
              )}
            </div>
          </div>

          {/* Separation of Generation: Banner prompting user to generate with AI or showing status */}
          {!hasDrafted && !isDrafting ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 rounded-lg bg-surface-input border border-surface-border">
              <div className="text-xs sm:text-sm text-text-primary">
                <span className="font-semibold text-text-primary">Generate with AI:</span> Click below to draft a structured <strong className="text-primary">{issueType}</strong> from the clicked message.
              </div>
              <Button
                type="button"
                onClick={() => handleGenerateDraft(issueType)}
                disabled={isBusy}
                className="bg-primary hover:bg-primary/90 text-white flex items-center gap-2 text-xs sm:text-sm h-8 px-3 whitespace-nowrap shadow-sm"
              >
                <Sparkles className="w-4 h-4" />
                Generate {issueType} Draft
              </Button>
            </div>
          ) : isDrafting ? (
            <div className="flex items-center gap-2.5 p-3 rounded-lg bg-surface-input border border-primary/40 text-xs sm:text-sm text-text-primary">
              <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />
              <span>Drafting <strong className="text-primary">{issueType}</strong> from the conversation using {issueType.toLowerCase()}-tuned template…</span>
            </div>
          ) : hasDrafted && issueType !== lastDraftedType ? (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-lg border border-primary/30 bg-primary/10 text-text-primary">
              <div className="text-xs sm:text-sm font-medium">
                Issue type changed to <span className="font-bold text-primary">{issueType}</span>{" "}
                <span className="text-muted font-normal">(previously drafted as {lastDraftedType})</span>.
              </div>
              <Button
                type="button"
                size="sm"
                onClick={() => handleGenerateDraft(issueType)}
                disabled={isBusy}
                className="h-8 px-3 text-xs bg-primary hover:bg-primary/90 text-white flex items-center gap-1.5 shadow-sm whitespace-nowrap"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Re-draft as {issueType}
              </Button>
            </div>
          ) : null}

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="block text-sm font-medium text-text-primary">
                Title *
              </Label>
              {hasDrafted && !isDrafting && issueType === lastDraftedType && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleGenerateDraft(issueType)}
                  disabled={isBusy}
                  className="h-7 px-2.5 text-xs text-text-primary border-surface-border hover:bg-surface-hover flex items-center gap-1.5"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Regenerate {issueType} Draft
                </Button>
              )}
            </div>
            {isDrafting ? (
              <div className="flex items-center gap-2 text-sm text-muted h-10">
                <Loader2 className="w-4 h-4 animate-spin" />
                Drafting title…
              </div>
            ) : (
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={255}
                placeholder="Short, imperative summary of the work"
                className="w-full h-9 bg-surface-input text-text-primary border border-surface-border placeholder:text-muted focus-visible:ring-2 focus-visible:ring-primary"
              />
            )}
          </div>

          {/* Description Section: Expandable via "+ Add description" or AI generation */}
          <div>
            {!isDescriptionOpen ? (
              <div>
                <Label className="block text-sm font-medium text-text-primary mb-1">
                  Description
                </Label>
                <button
                  type="button"
                  onClick={() => setIsDescriptionOpen(true)}
                  disabled={isBusy}
                  className="w-full py-2.5 px-4 rounded-lg border border-dashed border-surface-border hover:border-primary/60 bg-surface-input/70 hover:bg-surface-active text-sm font-normal text-text-primary hover:text-primary flex items-center justify-center gap-2 transition-colors shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add description
                </button>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="block text-sm font-medium text-text-primary">
                    Description
                  </Label>
                  <button
                    type="button"
                    onClick={() => {
                      setIsDescriptionOpen(false);
                      setDescription("");
                    }}
                    disabled={isBusy}
                    className="text-xs text-muted hover:text-red-400 flex items-center gap-1 transition-colors"
                    title="Remove description"
                  >
                    <X className="w-3.5 h-3.5" /> Remove description
                  </button>
                </div>
                {isDrafting ? (
                  <div className="flex items-center gap-2 text-sm text-muted h-40">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Drafting description…
                  </div>
                ) : (
                  <RichTextEditor
                    value={description}
                    onChange={setDescription}
                    placeholder="Markdown is supported"
                    label="Ticket description"
                  />
                )}
              </div>
            )}
          </div>

          {/* Dynamic Extra Fields */}
          {activeFields.includes("priority") && (
            <div className="rounded-lg border border-surface-border bg-surface-input/40 p-3 space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-text-primary">
                  Priority
                </Label>
                <button
                  type="button"
                  onClick={() => handleRemoveField("priority")}
                  disabled={isBusy}
                  className="text-xs text-muted hover:text-red-400 flex items-center gap-1 transition-colors"
                  title="Remove priority"
                >
                  <X className="w-3.5 h-3.5" /> Remove
                </button>
              </div>
              <RadixSelect
                value={priority}
                onChange={setPriority}
                placeholder="Select priority"
                disabled={isBusy}
                options={PRIORITY_OPTIONS}
              />
            </div>
          )}

          {activeFields.includes("labels") && (
            <div className="rounded-lg border border-surface-border bg-surface-input/40 p-3 space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-text-primary">
                  Labels
                </Label>
                <button
                  type="button"
                  onClick={() => handleRemoveField("labels")}
                  disabled={isBusy}
                  className="text-xs text-muted hover:text-red-400 flex items-center gap-1 transition-colors"
                  title="Remove labels"
                >
                  <X className="w-3.5 h-3.5" /> Remove
                </button>
              </div>
              <Input
                value={labelsInput}
                onChange={(e) => setLabelsInput(e.target.value)}
                placeholder="e.g. frontend, bug, v1.2 (comma-separated)"
                className="w-full h-9 bg-surface-input text-text-primary border border-surface-border placeholder:text-muted"
                disabled={isBusy}
              />
            </div>
          )}

          {activeFields.includes("environment") && (
            <div className="rounded-lg border border-surface-border bg-surface-input/40 p-3 space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-text-primary">
                  Environment
                </Label>
                <button
                  type="button"
                  onClick={() => handleRemoveField("environment")}
                  disabled={isBusy}
                  className="text-xs text-muted hover:text-red-400 flex items-center gap-1 transition-colors"
                  title="Remove environment"
                >
                  <X className="w-3.5 h-3.5" /> Remove
                </button>
              </div>
              <Input
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
                placeholder="e.g. Production, Staging, Chrome 120 / macOS"
                className="w-full h-9 bg-surface-input text-text-primary border border-surface-border placeholder:text-muted"
                disabled={isBusy}
              />
            </div>
          )}

          {/* "+ Add field" button */}
          {availableFields.length > 0 && (
            <div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isBusy}
                    className="h-8 px-3 text-xs border-dashed border-surface-border hover:border-primary/50 text-text-primary hover:bg-surface-hover flex items-center gap-1.5"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add field
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-surface border border-surface-border text-text-primary">
                  {availableFields.map((field) => (
                    <DropdownMenuItem
                      key={field.key}
                      onClick={() => handleAddField(field.key)}
                      className="cursor-pointer text-xs flex items-center gap-2 hover:bg-surface-hover"
                    >
                      <Plus className="w-3 h-3 text-muted" />
                      {field.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {/* Sibling to the scroll container, not inside it, so it can never
            overlap the fields. */}
        <div className="flex justify-end gap-3 px-4 sm:px-6 py-4 border-t border-surface-border">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
            className="bg-surface border border-surface-border hover:bg-surface-hover text-text-primary"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleCreate}
            disabled={!canSubmit}
            className="bg-primary hover:bg-primary/90 text-white disabled:bg-muted disabled:cursor-not-allowed"
          >
            {isCreating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating…
              </>
            ) : (
              "Create Ticket"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
